import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sessionService from '../../services/sessionService';
import { useTheme } from '../../contexts/ThemeContext';

interface CheckpointTimelineProps {
  sessionId: string;
  onCheckpointSelect: (checkpointId: string) => void;
}

export const CheckpointTimeline: React.FC<CheckpointTimelineProps> = ({
  sessionId,
  onCheckpointSelect
}) => {
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { darkMode } = useTheme();
  
  const { data: checkpoints, isLoading, error } = useQuery(
    ['checkpoints', sessionId],
    () => sessionService.getCheckpoints(sessionId),
    { enabled: !!sessionId }
  );
  
  const createCheckpointMutation = useMutation(
    (description: string) => sessionService.createCheckpoint(sessionId, description),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['checkpoints', sessionId]);
      }
    }
  );
  
  const handleCheckpointSelect = (checkpointId: string) => {
    setSelectedCheckpoint(checkpointId);
    onCheckpointSelect(checkpointId);
  };
  
  const handleCreateCheckpoint = () => {
    const description = prompt('Enter checkpoint description:');
    if (description) {
      createCheckpointMutation.mutate(description);
    }
  };
  
  if (isLoading) return <div className={`text-center py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Loading checkpoints...</div>;
  if (error) return <div className={`text-center py-4 text-red-600 ${darkMode ? 'bg-red-900 bg-opacity-20' : ''}`}>Error loading checkpoints</div>;
  
  return (
    <div className={`checkpoint-timeline rounded-lg shadow p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Session Checkpoints</h3>
        <button 
          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={handleCreateCheckpoint}
          disabled={createCheckpointMutation.isLoading}
        >
          {createCheckpointMutation.isLoading ? 'Creating...' : 'Create Checkpoint'}
        </button>
      </div>
      
      {checkpoints && checkpoints.length > 0 ? (
        <div className="relative pt-6 pb-2">
          {/* Timeline track */}
          <div className={`absolute top-6 left-0 right-0 h-1 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
          
          {/* Timeline points */}
          <div className="flex justify-between relative">
            {checkpoints.map((checkpoint, index) => (
              <div 
                key={checkpoint.id}
                className="flex flex-col items-center cursor-pointer"
                onClick={() => handleCheckpointSelect(checkpoint.id)}
              >
                <div className={`text-xs mb-2 w-20 text-center truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {new Date(checkpoint.created_at).toLocaleTimeString()}
                </div>
                <div 
                  className={`w-3 h-3 rounded-full z-10 ${
                    selectedCheckpoint === checkpoint.id
                      ? 'bg-blue-600 ring-4 ring-blue-100'
                      : darkMode ? 'bg-gray-500' : 'bg-gray-400'
                  }`}
                ></div>
                <div className={`text-xs mt-2 w-20 text-center truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {checkpoint.description || `Checkpoint ${index + 1}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No checkpoints available. Create one to save the current session state.
        </div>
      )}
    </div>
  );
};
